import { Dialog } from "primereact/dialog";

const statusLabel = {
  pending: "Syncing",
  completed: "Completed",
  failed: "Failed",
  unchanged: "No change",
};

const statusClass = {
  pending: "bg-yellow-100 text-yellow-700",
  completed: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  unchanged: "bg-gray-100 text-gray-700",
};

const quantity = (value) =>
  value === null || value === undefined ? "—" : Number(value).toLocaleString();

const adjustedAt = (value) => {
  if (!value) return "In progress";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const adjustedBy = (audit) =>
  audit.adjusted_by_name ||
  (audit.adjusted_by ? `User #${audit.adjusted_by}` : "System");

const qboInventory = (audit) => {
  if (
    audit.previous_qty_on_hand === null &&
    audit.qty_on_hand === null
  ) {
    return "—";
  }

  return `${quantity(audit.previous_qty_on_hand)} → ${quantity(
    audit.qty_on_hand,
  )}`;
};

export default function PackagingInventoryAdjustmentAuditDialog({
  open,
  onClose,
  onRetry,
  sku,
  productName,
  audits = [],
  loading = false,
  error = null,
}) {
  return (
    <Dialog
      visible={open}
      onHide={onClose}
      header={`QBO adjustment audit — ${sku || ""}`}
      style={{ width: "72rem", maxWidth: "96vw" }}
      breakpoints={{ "960px": "92vw", "640px": "98vw" }}
      modal
    >
      {productName && (
        <p className="mb-4 text-sm text-gray-500">{productName}</p>
      )}

      {loading ? (
        <div className="py-10 text-center text-sm text-gray-500">
          Loading audit history…
        </div>
      ) : error ? (
        <div
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 p-4"
        >
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-3 rounded-md border border-red-300 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
          >
            Retry
          </button>
        </div>
      ) : audits.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">
          No QBO adjustment history is available for this SKU.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-300">
            <thead>
              <tr>
                <th className="py-3 pr-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  When
                </th>
                <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Updated by
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Requested
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Previous sync
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  QBO change
                </th>
                <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                  QBO inventory
                </th>
                <th className="py-3 pl-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {audits.map((audit) => (
                <tr key={audit.id}>
                  <td className="whitespace-nowrap py-3 pr-3 text-sm text-gray-600">
                    {adjustedAt(audit.adjusted_at)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-600">
                    {adjustedBy(audit)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-900">
                    {quantity(audit.requested_quantity)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-600">
                    {quantity(audit.previous_synced_quantity)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm font-medium text-gray-900">
                    {Number(audit.quantity_adjusted) > 0 ? "+" : ""}
                    {quantity(audit.quantity_adjusted)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-right text-sm text-gray-600">
                    {qboInventory(audit)}
                  </td>
                  <td className="py-3 pl-3 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${
                        statusClass[audit.status] || statusClass.unchanged
                      }`}
                    >
                      {statusLabel[audit.status] || audit.status}
                    </span>
                    {audit.error && (
                      <p className="mt-1 max-w-xs whitespace-normal text-xs text-red-600">
                        {audit.error}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
